import { useEffect } from 'react';
import StatusCodeRenderer from '@/app/common/components/CellRenderers/StatusCodeRenderer';
import SubtleBadge from '@/components/common/SubtleBadge';
import NoHeaderAgGridSkeleton from '@/app/common/components/skeleton/NoHeaderAgGridSkeleton';
import { Accordion, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetByUrlQuery } from '@/app/Crawl/api/detailsApi';
import Url from '@/app/common/components/Url';
import useDomain from '@/hooks/useDomain';

type UrlDetails = {
  type?: string;
  content_type?: string;
  protocol?: string;
  url?: string;
  depth?: number | string;
  canonical_url?: string;
  status_code?: number;
  response_time?: number | string;
  header_key: string[];
  title?: string;
  meta_desc?: string;
  h1: string[];
  h2: string[];
  h3: string[];
  word_count?: number;
  unique_words_count?: number;
  char_count?: number;
  sentence_count?: number;
  low_word?: boolean;
  high_word?: boolean;
  reading_time?: number | string;
  published_time?: string;
  last_modified?: string;
  last_modified_by_day?: number;
  old_blog?: boolean;
  lang?: string;
};

type UrlInfoResponse = {
  status_code?: number;
  data?: UrlDetails;
};

const Overview = () => {
  const [searchParams] = useSearchParams();
  const { domain_slug } = useDomain();
  useGetUserInfoQuery();

  // نمای کبی جزییات صفحه;
  const { data: rawUrlInfo, isLoading } = useGetByUrlQuery({
    job_page: searchParams.get('job_page'),
    domain_slug,
    page_url: searchParams.get('page_url'),
    type: searchParams.get('type') || 'internal',
    // دریافت ستون ها جهت نمایش
    columns: [
      'type',
      'protocol',
      'title',
      'meta_desc',
      'url',
      'depth',
      'canonical_url',
      'status_code',
      'response_time',
      'content_type',
      'content_type',
      'h1',
      'h2',
      'h3',
      'lang',
      'header_key',
      'header_value',
      'word_count',
      'unique_words_count',
      'char_count',
      'sentence_count',
      'reading_time',
      'published_time',
      'last_modified',
      'last_modified_by_day',
      'old_blog',
      'high_word',
      'low_word'
    ]
  });
  const urlInfo = rawUrlInfo as UrlInfoResponse | undefined;
  useEffect(() => {
    if (urlInfo) {
      if (
        urlInfo.status_code === 200 ||
        Number(searchParams.get('status_code')) !== 200
      ) {
        searchParams.set('status_code', String(200));
      }
    }
  }, [urlInfo]);

  return (
    <div id="overview" className="mt-5">
      <Accordion defaultActiveKey={['0', '2']} alwaysOpen>
        <Accordion.Item eventKey="0">
          {/* منوی اطلاعات نشانتی */}
          <Accordion.Header>اطلاعات نشانی</Accordion.Header>
          <Accordion.Body>
            {!isLoading ? (
              <Table size="sm">
                <tbody>
                  <tr>
                    <td className="text-primary">نوع</td>
                    <td>{urlInfo?.data?.type}</td>
                  </tr>
                  <tr>
                    <td>نوع محتوا</td>
                    <td>{urlInfo?.data?.content_type}</td>
                  </tr>
                  <tr>
                    <td>پروتکل</td>
                    <td>{urlInfo?.data?.protocol}</td>
                  </tr>
                  <tr>
                    <td>آدرس</td>
                    <td>
                      <Url url={urlInfo?.data?.url} />
                    </td>
                  </tr>
                  <tr>
                    <td>کد وضعیت</td>
                    <td>
                      <StatusCodeRenderer
                        statusCode={urlInfo?.data?.status_code}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>عمق</td>
                    <td>{urlInfo?.data?.depth}</td>
                  </tr>
                  <tr>
                    <td>آدرس کنونیکال</td>
                    <td>
                      {urlInfo?.data?.canonical_url ? (
                        <Url url={urlInfo?.data?.canonical_url} />
                      ) : (
                        <Url url={urlInfo?.data?.url} />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>زمان پاسخ</td>
                    <td>
                      {Number(urlInfo?.data?.response_time).toFixed(1)} ثانیه
                    </td>
                  </tr>
                </tbody>
              </Table>
            ) : (
              <NoHeaderAgGridSkeleton height="289px" />
            )}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          {/* منوی اطلاعات هدر */}
          <Accordion.Header>اطلاعات هدر</Accordion.Header>
          <Accordion.Body>
            {!isLoading ? (
              <Table size="sm">
                <tbody>
                  {urlInfo?.data?.header_key.map((item: string, index: number) => (
                    <tr key={index}>
                      <td>{item}</td>
                      <td>{urlInfo?.data?.header_key[index]}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <NoHeaderAgGridSkeleton height="289px" />
            )}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          {/* منوی اطلاعات محتوا */}
          <Accordion.Header>اطلاعات محتوا</Accordion.Header>
          <Accordion.Body>
            {!isLoading ? (
              <Table size="sm">
                <tbody>
                  <tr>
                    <td>عنوان صفحه</td>
                    <td>
                      {urlInfo?.data?.title ? (
                        urlInfo?.data?.title
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>توضیح متا</td>
                    <td>
                      {urlInfo?.data?.meta_desc ? (
                        urlInfo?.data?.meta_desc
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>H1</td>
                    <td>
                      {urlInfo?.data?.h1.length !== 0 ? (
                        <ul className="p-0">
                          {urlInfo?.data?.h1.map((item: string) => (
                            <li key={item} className="mb-1">
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>H2</td>
                    <td>
                      {urlInfo?.data?.h2.length !== 0 ? (
                        <ul className="p-0">
                          {urlInfo?.data?.h2.map((item: string) => (
                            <li key={item} className="mb-1">
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    {' '}
                    {/* منوی اطلاعات h3 */}
                    <td>H3</td>
                    <td>
                      {urlInfo?.data?.h3.length !== 0 ? (
                        <ul className="p-0">
                          {urlInfo?.data?.h3.map((item: string) => (
                            <li key={item} className="mb-1">
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تعداد کلمات</td>
                    <td>
                      {urlInfo?.data?.word_count ? (
                        urlInfo?.data?.word_count
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تعداد کلمات منحصر به فرد</td>
                    <td>
                      {urlInfo?.data?.unique_words_count ? (
                        urlInfo?.data?.unique_words_count
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تعداد کاراکتر</td>
                    <td>
                      {urlInfo?.data?.char_count ? (
                        urlInfo?.data?.char_count
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تعداد جملات</td>
                    <td>
                      {urlInfo?.data?.sentence_count ? (
                        urlInfo?.data?.sentence_count
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>کم محتوا هست؟</td>
                    <td>
                      {urlInfo?.data?.low_word ? (
                        <SubtleBadge bg="warning" className="me-2">
                          بله
                        </SubtleBadge>
                      ) : (
                        <SubtleBadge bg="success" className="me-2">
                          خیر
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>محتوای بیش از اندازه داره؟</td>
                    <td>
                      {urlInfo?.data?.high_word ? (
                        <SubtleBadge bg="warning" className="me-2">
                          بله
                        </SubtleBadge>
                      ) : (
                        <SubtleBadge bg="success" className="me-2">
                          خیر
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>مدت زمان مطالعه</td>
                    <td>
                      {urlInfo?.data?.reading_time ? (
                        <span>
                          {Number(urlInfo?.data?.reading_time).toFixed(1)} دقیقه
                        </span>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تاریخ انتشار</td>
                    <td>
                      {urlInfo?.data?.published_time ? (
                        urlInfo?.data?.published_time
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          اطلاعاتی موجود نبود
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>تاریخ آخرین ویرایش</td>
                    <td>
                      {urlInfo?.data?.last_modified ? (
                        urlInfo?.data?.last_modified
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          اطلاعاتی موجود نبود
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>چند روزه صفحه آپدیت نشده؟</td>
                    <td>
                      {urlInfo?.data?.last_modified_by_day ? (
                        <span>{urlInfo?.data?.last_modified_by_day} روز</span>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          اطلاعاتی موجود نبود
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>مقاله قدیمی شده؟</td>
                    <td>
                      {urlInfo?.data?.old_blog ? (
                        <SubtleBadge bg="warning" className="me-2">
                          بله
                        </SubtleBadge>
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          خیر
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td>زبان صفحه</td>
                    <td>
                      {urlInfo?.data?.lang ? (
                        urlInfo?.data?.lang
                      ) : (
                        <SubtleBadge bg="warning" className="me-2">
                          ندارد
                        </SubtleBadge>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            ) : (
              <NoHeaderAgGridSkeleton height="289px" />
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <style>
        {`
          .table {
            width: auto !important;
            font-size: 14px;
          }

          .table > :not(caption) > * > *{
            color:var(--falcon-gray-800) !important;
          }


          .accordion-button:not(.collapsed),.accordion-button{
            color:var(--falcon-gray-900) !important;
            font-weight: 500;
          }

          thead, tbody, tfoot, tr, td, th {
            border-style: hidden;
          }

          ul{
            list-style-type: auto;
          }
          
          .table tfoot > tr > th:last-child, .table thead > tr > th:last-child, .table tr th:last-child, .table tr td:last-child {
            padding-right: 1.25rem;
          }
        `}
      </style>
    </div>
  );
};
export default Overview;
